import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { SwalAlert } from '@/hooks/useSweetAlert';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

type LoadingState = 'loading' | 'ready' | 'error';
type PeriodKey = 'monthly' | 'quarterly' | 'annual';

const revenueData = {
  monthly: {
    mrr: 284500,
    arr: 3414000,
    arpu: 18.75,
    conversions: 342,
    churnRate: 3.2,
    renewals: 1250,
    refunds: 18,
    disputes: 5,
    revenueByPlan: [
      { plan: 'Free', revenue: 0, count: 12800 },
      { plan: 'Básico', revenue: 42500, count: 2834 },
      { plan: 'Estándar', revenue: 98000, count: 1567 },
      { plan: 'Pro', revenue: 89000, count: 890 },
      { plan: 'Premium', revenue: 55000, count: 367 },
    ],
    revenueByCountry: [
      { country: 'Colombia', flag: '🇨🇴', revenue: 98500, pct: 34.6 },
      { country: 'México', flag: '🇲🇽', revenue: 72000, pct: 25.3 },
      { country: 'Argentina', flag: '🇦🇷', revenue: 38500, pct: 13.5 },
      { country: 'España', flag: '🇪🇸', revenue: 28200, pct: 9.9 },
      { country: 'Chile', flag: '🇨🇱', revenue: 18500, pct: 6.5 },
      { country: 'Perú', flag: '🇵🇪', revenue: 12800, pct: 4.5 },
      { country: 'Otros', flag: '🌎', revenue: 16000, pct: 5.7 },
    ],
    revenueBySource: [
      { source: 'Planes', revenue: 242500, pct: 85.2 },
      { source: 'Publicidad', revenue: 28500, pct: 10.0 },
      { source: 'Licencias Anuales', revenue: 8500, pct: 3.0 },
      { source: 'Marketplace', revenue: 5000, pct: 1.8 },
    ],
    monthlyTrend: [
      { month: 'Ene', revenue: 245000 }, { month: 'Feb', revenue: 252000 }, { month: 'Mar', revenue: 260000 },
      { month: 'Abr', revenue: 268000 }, { month: 'May', revenue: 274000 }, { month: 'Jun', revenue: 284500 },
    ],
  },
  quarterly: {
    mrr: 284500,
    arr: 3414000,
    arpu: 18.75,
    conversions: 1026,
    churnRate: 2.8,
    renewals: 3750,
    refunds: 52,
    disputes: 12,
    revenueByPlan: [
      { plan: 'Free', revenue: 0, count: 12800 },
      { plan: 'Básico', revenue: 127500, count: 2834 },
      { plan: 'Estándar', revenue: 294000, count: 1567 },
      { plan: 'Pro', revenue: 267000, count: 890 },
      { plan: 'Premium', revenue: 165000, count: 367 },
    ],
    revenueByCountry: [
      { country: 'Colombia', flag: '🇨🇴', revenue: 295500, pct: 34.6 },
      { country: 'México', flag: '🇲🇽', revenue: 216000, pct: 25.3 },
      { country: 'Argentina', flag: '🇦🇷', revenue: 115500, pct: 13.5 },
      { country: 'España', flag: '🇪🇸', revenue: 84600, pct: 9.9 },
      { country: 'Chile', flag: '🇨🇱', revenue: 55500, pct: 6.5 },
      { country: 'Perú', flag: '🇵🇪', revenue: 38400, pct: 4.5 },
      { country: 'Otros', flag: '🌎', revenue: 48000, pct: 5.7 },
    ],
    revenueBySource: [
      { source: 'Planes', revenue: 727500, pct: 85.2 },
      { source: 'Publicidad', revenue: 85500, pct: 10.0 },
      { source: 'Licencias Anuales', revenue: 25500, pct: 3.0 },
      { source: 'Marketplace', revenue: 15000, pct: 1.8 },
    ],
    monthlyTrend: [
      { month: 'Ene', revenue: 245000 }, { month: 'Feb', revenue: 252000 }, { month: 'Mar', revenue: 260000 },
      { month: 'Abr', revenue: 268000 }, { month: 'May', revenue: 274000 }, { month: 'Jun', revenue: 284500 },
    ],
  },
  annual: {
    mrr: 284500,
    arr: 3414000,
    arpu: 18.75,
    conversions: 4104,
    churnRate: 2.5,
    renewals: 15000,
    refunds: 208,
    disputes: 48,
    revenueByPlan: [
      { plan: 'Free', revenue: 0, count: 12800 },
      { plan: 'Básico', revenue: 510000, count: 2834 },
      { plan: 'Estándar', revenue: 1176000, count: 1567 },
      { plan: 'Pro', revenue: 1068000, count: 890 },
      { plan: 'Premium', revenue: 660000, count: 367 },
    ],
    revenueByCountry: [
      { country: 'Colombia', flag: '🇨🇴', revenue: 1182000, pct: 34.6 },
      { country: 'México', flag: '🇲🇽', revenue: 864000, pct: 25.3 },
      { country: 'Argentina', flag: '🇦🇷', revenue: 462000, pct: 13.5 },
      { country: 'España', flag: '🇪🇸', revenue: 338400, pct: 9.9 },
      { country: 'Chile', flag: '🇨🇱', revenue: 222000, pct: 6.5 },
      { country: 'Perú', flag: '🇵🇪', revenue: 153600, pct: 4.5 },
      { country: 'Otros', flag: '🌎', revenue: 192000, pct: 5.7 },
    ],
    revenueBySource: [
      { source: 'Planes', revenue: 2904000, pct: 85.2 },
      { source: 'Publicidad', revenue: 342000, pct: 10.0 },
      { source: 'Licencias Anuales', revenue: 102000, pct: 3.0 },
      { source: 'Marketplace', revenue: 60000, pct: 1.8 },
    ],
    monthlyTrend: [
      { month: 'Ene', revenue: 245000 }, { month: 'Feb', revenue: 252000 }, { month: 'Mar', revenue: 260000 },
      { month: 'Abr', revenue: 268000 }, { month: 'May', revenue: 274000 }, { month: 'Jun', revenue: 284500 },
    ],
  },
};

function KpiCard({ label, value, icon, bg, color, subtitle }: {
  label: string; value: string; icon: string; bg: string; color: string; subtitle?: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
        {subtitle && <p className="text-foreground-400 text-[9px]">{subtitle}</p>}
      </div>
    </div>
  );
}

export default function SuperAdminRevenueAnalytics() {
  const [loadState, setLoadState] = useState<LoadingState>('loading');
  const [period, setPeriod] = useState<PeriodKey>('monthly');

  useEffect(() => {
    const d = 500 + Math.random() * 300;
    const timer = setTimeout(() => setLoadState(Math.random() < 0.06 ? 'error' : 'ready'), d);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setLoadState('loading');
    setTimeout(() => setLoadState(Math.random() < 0.04 ? 'error' : 'ready'), 400);
  }, []);

  const handleExport = async () => {
    const r = await SwalAlert.confirm('Exportar CSV', '¿Exportar los datos visibles como CSV?');
    if (r.isConfirmed) SwalAlert.success('Exportado', 'Archivo generado exitosamente (mock).');
  };

  const data = revenueData[period];

  const renderLayout = (children: React.ReactNode) => (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Revenue Analytics</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Revenue Analytics</h2>
                <p className="text-foreground-400 text-xs mt-0.5">Análisis de ingresos · No incluye operaciones de Billing</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-1 py-1 rounded-full bg-background-100">
                  {[{ key: 'monthly' as const, label: 'Mensual' }, { key: 'quarterly' as const, label: 'Trimestral' }, { key: 'annual' as const, label: 'Anual' }].map((p) => (
                    <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${period === p.key ? 'bg-foreground-950 text-background-50 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}>{p.label}</button>
                  ))}
                </div>
                <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-600 text-[10px] font-medium cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap">
                  <i className="ri-file-excel-2-line text-[10px]"></i>Exportar CSV
                </button>
              </div>
            </div>
            {children}
          </div>
        </main>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Revenue Analytics</p>
      </div>
    </div>
  );

  if (loadState === 'loading') {
    return renderLayout(
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 rounded-xl bg-background-200"></div>
              <div className="space-y-1"><div className="w-16 h-2.5 bg-background-200 rounded"></div><div className="w-10 h-4 bg-background-200 rounded"></div></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return renderLayout(
      <div className="flex items-center justify-center py-12">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><i className="ri-error-warning-line text-red-500 text-2xl"></i></div>
          <h2 className="text-foreground-950 font-bold text-base mb-1.5">Error cargando datos</h2>
          <p className="text-foreground-400 text-xs mb-5">No se pudieron cargar los datos de revenue.</p>
          <button onClick={handleRetry} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
        </div>
      </div>
    );
  }

  return renderLayout(
    <div className="space-y-5">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        <KpiCard label="MRR" value={formatCurrency(data.mrr)} icon="ri-money-dollar-circle-line" bg="bg-emerald-50" color="text-emerald-600" />
        <KpiCard label="ARR" value={formatCurrency(data.arr)} icon="ri-funds-line" bg="bg-primary-50" color="text-primary-500" />
        <KpiCard label="ARPU" value={`$${data.arpu}`} icon="ri-user-line" bg="bg-violet-50" color="text-violet-500" />
        <KpiCard label="Conversiones" value={formatNumber(data.conversions)} icon="ri-exchange-line" bg="bg-amber-50" color="text-amber-500" />
        <KpiCard label="Churn Rate" value={`${data.churnRate}%`} icon="ri-arrow-down-circle-line" bg="bg-red-50" color="text-red-500" />
        <KpiCard label="Renovaciones" value={formatNumber(data.renewals)} icon="ri-refresh-line" bg="bg-sky-50" color="text-sky-500" />
        <KpiCard label="Reembolsos" value={String(data.refunds)} icon="ri-arrow-go-back-line" bg="bg-orange-50" color="text-orange-500" />
        <KpiCard label="Disputas" value={String(data.disputes)} icon="ri-error-warning-line" bg="bg-stone-50" color="text-stone-500" />
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <h3 className="text-foreground-800 text-sm font-bold mb-4">Evolución de Revenue</h3>
        <div className="flex items-end gap-3 h-[140px]">
          {data.monthlyTrend.map((item) => {
            const maxV = Math.max(...data.monthlyTrend.map((d) => d.revenue));
            const pct = (item.revenue / maxV) * 100;
            return (
              <div key={item.month} className="flex-1 flex flex-col items-center gap-1.5">
                <span className="text-foreground-800 text-[10px] font-bold">{formatCurrency(item.revenue)}</span>
                <div className="w-full bg-emerald-400 rounded-t-lg transition-all duration-500 hover:bg-emerald-500 cursor-pointer" style={{ height: `${pct}%`, minHeight: '8px' }}></div>
                <span className="text-foreground-400 text-[9px]">{item.month}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue by Plan */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
          <h3 className="text-foreground-800 text-sm font-bold mb-4">Ingresos por Plan</h3>
          <div className="space-y-3">
            {data.revenueByPlan.map((item) => {
              const maxV = Math.max(...data.revenueByPlan.map((d) => d.revenue), 1);
              const pct = Math.round((item.revenue / maxV) * 100);
              return (
                <div key={item.plan}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-foreground-500 text-[10px] font-medium">{item.plan}</span>
                    <span className="text-foreground-800 text-[10px] font-bold">
                      {item.revenue === 0 ? '-' : formatCurrency(item.revenue)}
                      <span className="text-foreground-400 text-[8px] ml-1">({formatNumber(item.count)} radios)</span>
                    </span>
                  </div>
                  <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary-400 rounded-full" style={{ width: `${Math.max(pct, 4)}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Revenue by Country */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
          <h3 className="text-foreground-800 text-sm font-bold mb-4">Ingresos por País</h3>
          <div className="space-y-2.5">
            {data.revenueByCountry.map((item) => (
              <div key={item.country} className="flex items-center gap-3">
                <span className="text-sm w-6 text-center">{item.flag}</span>
                <span className="text-foreground-500 text-[10px] font-medium flex-1">{item.country}</span>
                <div className="flex-1 h-2 bg-background-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${item.pct}%` }}></div>
                </div>
                <span className="text-foreground-800 text-[10px] font-bold whitespace-nowrap">{formatCurrency(item.revenue)}</span>
                <span className="text-foreground-400 text-[8px] w-8 text-right">{item.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue by Source */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <h3 className="text-foreground-800 text-sm font-bold mb-4">Ingresos por Fuente</h3>
        <div className="flex items-stretch gap-2 h-12 rounded-xl overflow-hidden">
          {data.revenueBySource.map((item) => (
            <div key={item.source} className="relative flex items-center justify-center transition-all cursor-default" style={{ flexBasis: `${item.pct}%` }}>
              <div className="absolute inset-0 bg-emerald-400 opacity-80" style={{ opacity: 0.6 + (item.pct / 100) * 0.4 }}></div>
              <span className="relative text-background-50 text-[10px] font-bold whitespace-nowrap px-2">{item.source} {item.pct}%</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-3 mt-3">
          {data.revenueBySource.map((item) => (
            <div key={item.source} className="text-center">
              <p className="text-foreground-800 text-sm font-bold">{formatCurrency(item.revenue)}</p>
              <p className="text-foreground-400 text-[9px]">{item.source}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><i className="ri-information-line text-accent-500 text-lg"></i></div>
          <div>
            <p className="text-accent-800 text-xs font-semibold">Revenue Analytics — Solo Análisis</p>
            <p className="text-accent-600/80 text-[10px]">Revenue Analytics analiza y visualiza los ingresos. Para operaciones de facturación, pagos y renovaciones, usá el módulo de Billing.</p>
          </div>
        </div>
      </div>
    </div>
  );
}